import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    CustomerPipelineFilters,
    BestEmployeeContactsItem,
    BestEmployeeConversionItem,
} from '../types'
import {
    getBestEmployeesContacts,
    getBestEmployeesConversion,
    exportBestEmployeesContacts,
    exportBestEmployeesConversion,
} from '../services'
import { Loading, Typogaphy } from 'vnetwork-ui'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { Trophy, Download, Users, Target } from 'lucide-react'
import {
    DataTableCustomized,
    createColumns,
} from '@/components/ui/data-table-customized'

interface BestEmployeesProps {
    filters: CustomerPipelineFilters
}

const RANK_COLORS: Record<number, string> = {
    1: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    2: 'bg-gray-100 text-gray-700 border-gray-300',
    3: 'bg-amber-100 text-amber-800 border-amber-300',
}

const BestEmployees: React.FC<BestEmployeesProps> = ({ filters }) => {
    const { t } = useTranslation()
    const [activeTab, setActiveTab] = useState<'contacts' | 'conversion'>(
        'contacts'
    )
    const [isExporting, setIsExporting] = useState(false)

    const queryFilters = {
        gte: filters.gte,
        lte: filters.lte,
        limit: 10,
    }

    // Best Employees by Contacts
    const { data: contactsData, renderState: renderContactsState } =
        useQueryWithHandler(
            {
                queryKey: [
                    QUERY_KEYS.REPORTS_SALE_PERFORMANCES,
                    'best-employees-contacts',
                    filters.gte,
                    filters.lte,
                ],
                queryFn: () => getBestEmployeesContacts(queryFilters),
                enabled: !!filters.gte && !!filters.lte,
            },
            {
                loadingComponent: (
                    <div className="flex justify-center items-center h-[300px]">
                        <Loading.CircularProgress width={32} height={32} />
                    </div>
                ),
            }
        )

    // Best Employees by Conversion
    const { data: conversionData, renderState: renderConversionState } =
        useQueryWithHandler(
            {
                queryKey: [
                    QUERY_KEYS.REPORTS_SALE_PERFORMANCES,
                    'best-employees-conversion',
                    filters.gte,
                    filters.lte,
                ],
                queryFn: () => getBestEmployeesConversion(queryFilters),
                enabled: !!filters.gte && !!filters.lte,
            },
            {
                loadingComponent: (
                    <div className="flex justify-center items-center h-[300px]">
                        <Loading.CircularProgress width={32} height={32} />
                    </div>
                ),
            }
        )

    const handleExport = async () => {
        setIsExporting(true)
        try {
            const blob =
                activeTab === 'contacts'
                    ? await exportBestEmployeesContacts(queryFilters)
                    : await exportBestEmployeesConversion(queryFilters)

            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `best_employees_${activeTab}_${filters.gte}_${filters.lte}.xlsx`
            document.body.appendChild(a)
            a.click()
            window.URL.revokeObjectURL(url)
            document.body.removeChild(a)
        } catch (error) {
            console.error('Export failed:', error)
        } finally {
            setIsExporting(false)
        }
    }

    const renderRankBadge = (rank: number) => {
        const colorClass =
            RANK_COLORS[rank] || 'bg-blue-50 text-blue-700 border-blue-200'
        return (
            <span
                className={`inline-flex items-center justify-center w-5 h-5 rounded-full border ${colorClass} font-medium text-xs`}
            >
                {rank <= 3 ? <Trophy size={8} /> : rank}
            </span>
        )
    }

    // Columns for Contacts table
    const contactsColumns = useMemo(
        () =>
            createColumns<BestEmployeeContactsItem>([
                {
                    key: 'rank',
                    title: t('rank'),
                    headerClassName: 'text-left uppercase',
                    width: 80,
                    render: (value) => renderRankBadge(value),
                },
                {
                    key: 'employee_name',
                    title: t('employee'),
                    headerClassName: 'text-left uppercase',
                    render: (_, employee) => employee?.employee_name,
                },
                {
                    key: 'total_contacts',
                    title: t('total_contacts'),
                    headerClassName: 'text-right uppercase',
                    cellClassName: 'text-right',
                    width: 150,
                    render: (value) => value.toLocaleString(),
                },
            ]),
        [t]
    )

    // Columns for Conversion table
    const conversionColumns = useMemo(
        () =>
            createColumns<BestEmployeeConversionItem>([
                {
                    key: 'rank',
                    title: t('rank'),
                    headerClassName: 'text-left uppercase',
                    width: 80,
                    render: (value) => renderRankBadge(value),
                },
                {
                    key: 'employee_name',
                    title: t('employee'),
                    headerClassName: 'text-left uppercase',
                    render: (_, emp) => (
                        <div className="flex gap-1 items-center">
                            <span className="font-medium text-gray-900">
                                {emp.employee_name}
                            </span>
                            <span className="text-gray-500">
                                #{emp.employee_id}
                            </span>
                        </div>
                    ),
                },
                {
                    key: 'total_contacts',
                    title: t('contacts'),
                    headerClassName: 'text-right uppercase',
                    cellClassName: 'text-right text-gray-700',
                    width: 250,
                    render: (value) => value.toLocaleString(),
                },
                {
                    key: 'total_poc',
                    title: t('poc'),
                    headerClassName: 'text-right uppercase',
                    cellClassName: 'text-right text-amber-600',
                    width: 150,
                    render: (value) => value.toLocaleString(),
                },
                {
                    key: 'total_close_win',
                    title: t('close_win'),
                    headerClassName: 'text-right uppercase',
                    cellClassName: 'text-right text-green-600 font-bold',
                    width: 150,
                    render: (value) => value.toLocaleString(),
                },
                {
                    key: 'contacts_to_poc_rate',
                    title: t('contacts_to_poc'),
                    headerClassName: 'text-right uppercase',
                    cellClassName: 'text-right',
                    width: 150,
                    render: (value) => (
                        <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-medium">
                            {value.toFixed(1)}%
                        </span>
                    ),
                },
                {
                    key: 'poc_to_close_win_rate',
                    title: t('poc_to_close_win'),
                    headerClassName: 'text-right uppercase',
                    cellClassName: 'text-right',
                    width: 250,
                    render: (value) => (
                        <span className="px-1.5 py-0.5 rounded bg-green-50 text-green-700 font-medium">
                            {value.toFixed(1)}%
                        </span>
                    ),
                },
            ]),
        [t]
    )

    return (
        <div className="bg-white rounded-md p-4 shadow-sm border border-gray-100 h-full flex flex-col">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <Typogaphy className="font-semibold text-primary">
                        {t('best_employees')}
                    </Typogaphy>
                    <Typogaphy className="text-xs text-gray-500">
                        {t('top_performers_in_period')}
                    </Typogaphy>
                </div>
                <button
                    onClick={handleExport}
                    disabled={isExporting}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 disabled:opacity-50"
                >
                    <Download size={14} />
                    {isExporting ? t('exporting') : t('export')}
                </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-3">
                <button
                    onClick={() => setActiveTab('contacts')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        activeTab === 'contacts'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                    <Users size={14} />
                    {t('by_contacts_input')}
                </button>
                <button
                    onClick={() => setActiveTab('conversion')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        activeTab === 'conversion'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                    <Target size={14} />
                    {t('by_conversion')}
                </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden">
                {activeTab === 'contacts' ? (
                    <>
                        {renderContactsState() ||
                            (contactsData && contactsData.length > 0 ? (
                                <DataTableCustomized
                                    pinnedHeader
                                    size="small"
                                    columns={contactsColumns}
                                    rows={contactsData}
                                    maxHeight="320px"
                                    rowKey={(row) => String(row.employee_id)}
                                />
                            ) : (
                                <div className="flex justify-center items-center h-[200px] text-gray-500">
                                    {t('no_data_available')}
                                </div>
                            ))}
                    </>
                ) : (
                    <>
                        {renderConversionState() ||
                            (conversionData && conversionData.length > 0 ? (
                                <DataTableCustomized
                                    pinnedHeader
                                    columns={conversionColumns}
                                    rows={conversionData}
                                    maxHeight="320px"
                                    rowKey={(row) => String(row.employee_id)}
                                />
                            ) : (
                                <div className="flex justify-center items-center h-[200px] text-gray-500">
                                    {t('no_data_available')}
                                </div>
                            ))}
                    </>
                )}
            </div>
        </div>
    )
}

export default BestEmployees
